import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/shared/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../../../components/ui/dropdown-menu";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import { useIsTemplate } from "../../hooks/useIsTemplate";
import { useDateFilters } from "../../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { usePermissions } from "../../hooks/usePermissions";

function BulkActions({ openActionsModal, openCopyModal, openDeleteModal, openImportModal }: any) {
    const { t } = useTranslation();
    const { mode } = useDateFilters();
    const { selectedEvents, selectedUnassignedEvents, events, calendarEvents, unassignedEvents } = useScheduleContext();
    const isTemplate = useIsTemplate();

    const {
        canDelete: canDeleteEvent,
        canCopy: canCopyEvent,
        canLock: canLockEvent,
        canUnlock: canUnlockEvent,
        canApprove: canApproveEvent,
        canImport,
        canDeleteUnassignedShift
    } = usePermissions();

    const canApprove = useMemo(() => {
        const data = mode !== "month"
            ? events
            : Object.values(calendarEvents || {}).flatMap((item: any) => item?.events || []);
        const list = selectedEvents?.length
            ? selectedEvents
            : data.filter((event: any) => event?.type !== "unavailable");
        return list?.some((event: any) => event.status === "pending" && !event.locked) || false;
    }, [events, selectedEvents, calendarEvents, mode]);

    const canUnapprove = useMemo(() => {
        const data = mode !== "month"
            ? events
            : Object.values(calendarEvents || {}).flatMap((item: any) => item?.events || []);
        const list = selectedEvents?.length
            ? selectedEvents
            : data.filter((event: any) => event?.type !== "unavailable");
        return list?.some((event: any) => event.status === "approved" && !event.locked) || false;
    }, [events, selectedEvents, calendarEvents, mode]);

    const canLock = useMemo(() => {
        const data = mode !== "month"
            ? events
            : Object.values(calendarEvents || {}).flatMap((item: any) => item?.events || []);
        const list = selectedEvents?.length
            ? selectedEvents
            : data.filter((event: any) => event?.type !== "unavailable");
        return list?.some((event: any) => !event.locked) || false;
    }, [events, selectedEvents, calendarEvents, mode]);

    const canUnlock = useMemo(() => {
        const data = mode !== "month"
            ? events
            : Object.values(calendarEvents || {}).flatMap((item: any) => item?.events || []);
        const list = selectedEvents?.length
            ? selectedEvents
            : data.filter((event: any) => event?.type !== "unavailable");
        return list?.some((event: any) => event.locked) || false;
    }, [events, selectedEvents, calendarEvents, mode]);

    const canDelete = useMemo(() => {
        const data = mode !== "month"
            ? events
            : Object.values(calendarEvents || {}).flatMap((item: any) => item?.events || []);
        const list = selectedEvents?.length
            ? selectedEvents
            : data.filter((event: any) => event?.type !== "unavailable");
        if (!list?.length) return false;

        return !list.every((event: any) => event.locked);
    }, [events, selectedEvents, calendarEvents, mode]);

    const canCopy = useMemo(() => {
        const data = mode !== "month"
            ? events
            : Object.values(calendarEvents || {}).flatMap((item: any) => item?.events || []);
        const _events = data.filter((event: any) => event?.type !== "unavailable");
        return !!_events?.length && !selectedEvents?.length;
    }, [events, selectedEvents, calendarEvents, mode]);

    // in month mode nothing selected means "delete everything in view", and the view owns the unassigned shifts too
    const unassignedIds = useMemo(() => {
        const hasSelection = selectedEvents?.length > 0 || selectedUnassignedEvents?.length > 0;
        if (mode !== "month" || hasSelection || !canDeleteUnassignedShift) return [];
        return (unassignedEvents || []).map(({ id }: { id: string }) => id);
    }, [mode, selectedEvents, selectedUnassignedEvents, canDeleteUnassignedShift, unassignedEvents]);

    const copy = useCallback(() => {
        const ids = selectedEvents?.map(({ id }: { id: string }) => id);
        openCopyModal({ ids, type: "bulk", isTemplate });
    }, [openCopyModal, selectedEvents, isTemplate]);

    const remove = useCallback(() => {
        const ids = selectedEvents?.map(({ id }: { id: string }) => id);
        openDeleteModal({
            ids,
            type: "bulk",
            isTemplate,
            ...(unassignedIds.length > 0 && {
                unassignedIds,
                hasEvents: canDelete && canDeleteEvent
            })
        });
    }, [openDeleteModal, selectedEvents, isTemplate, unassignedIds, canDelete, canDeleteEvent]);

    const submit = useCallback((action: string) => {
        const ids = selectedEvents?.map(({ id }: { id: string }) => id);
        openActionsModal({
            ids,
            action,
            type: "bulk"
        });
    }, [openActionsModal, selectedEvents]);

    return (canDeleteEvent || canCopyEvent || canLockEvent || canUnlockEvent || canApproveEvent || canImport) ? (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline">
                    {t("actions")}
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="tw-p-2" align="start">
                <DropdownMenuItem
                    className="tw-cursor-pointer"
                    disabled={!canCopy || !canCopyEvent}
                    onClick={copy}
                    hidden={isTemplate}
                >
                    {t("copy")}
                </DropdownMenuItem>

                <DropdownMenuItem
                    className="tw-cursor-pointer"
                    disabled={!canImport}
                    onClick={openImportModal}
                    hidden={true /**type !== "team"*/}
                >
                    {t("import")}
                </DropdownMenuItem>

                <DropdownMenuItem
                    className="tw-cursor-pointer"
                    onClick={() => submit("publish")}
                    disabled={!canApprove || !canApproveEvent}
                    hidden={isTemplate}
                >
                    {t("publish")}
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="tw-cursor-pointer"
                    onClick={() => submit("unpublish")}
                    disabled={!canUnapprove || !canApproveEvent}
                    hidden={isTemplate}
                >
                    {t("unpublish")}
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="tw-cursor-pointer"
                    onClick={() => submit("lock")}
                    disabled={!canLock || !canLockEvent}
                    hidden={isTemplate}
                >
                    {t("lock")}
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="tw-cursor-pointer"
                    onClick={() => submit("unlock")}
                    disabled={!canUnlock || !canUnlockEvent}
                    hidden={isTemplate}
                >
                    {t("unlock")}
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="tw-cursor-pointer"
                    disabled={(!canDelete || !canDeleteEvent) && unassignedIds.length === 0}
                    onClick={remove}
                >
                    {t("delete")}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    ) : null;
}

export default BulkActions;
